// The Digitain bet-builder (same-match acca) placement payload.
//
// Captured LIVE on Parikara 2026-09-10 (a real 0.5 TRY "Bet Builder (2)" bet on
// Liverpool–Fulham): the native UI places it with
//
//   POST /Betting/PlaceBet
//   { cp: <coupon>, countryCode, langId, partnerId }
//
// The same-match combo is ONE coupon item — `BetTypeName:"COUPON_ORDINAR_NAME"`,
// `StakeCount:1` — whose legs ride in a `BetBuilderStakes[]` array, not a
// multi-event express. `BoostType:0` always: the boosted promo odds are a
// coupon-level bonus (`LoadDayExpress`) that a hand-built combo never carries.
//
// Every field below is reproduced verbatim from that captured body. The only
// place this builder invents is where the live payload carried a template-bound
// value (partnerId / langId / countryCode / currency) — those are passed in, never
// hard-coded.

/** One leg of a same-match bet-builder combo — the Digitain stake + its market
 *  metadata, exactly as `/Betting/PlaceBet` wants it in `BetBuilderStakes[]`.
 *  Field names mirror Digitain's `Stake` object from `GetBetBuilderStakesByEvent`. */
export interface BetBuilderLeg {
  /** Digitain stake id (rotates when a market is taken down/re-opened). */
  stakeId: number;
  /** Digitain StakeType id — 1 = Исход (1X2), 3 = Тотал, 37 = double chance, … */
  stakeTypeId: number;
  /** Localized market name, e.g. "Исход", "Тотал". */
  stakeTypeName: string;
  /** Localized selection, e.g. "П1", "Больше", "X". */
  stakeName: string;
  /** The line for total/handicap markets (e.g. 3.5); null for 1X2. */
  argument: number | null;
  /** Digitain `SC` — 1=П1/Больше, 2=X/Меньше, 3=П2. */
  stakeCode: number;
  /** Current decimal odds (`F` on the raw stake). NOT serialised into the coupon —
   *  used only to compute the combined `Factor`. Absent when the board sent no price. */
  odds?: number;
  /** ⛔⛔ THE SUB-EVENT THIS STAKE BELONGS TO, and it is NOT always the fixture.
   *  A Digitain fixture is a tree: the main book plus «Угловые» (corners),
   *  «Желтые карточки» (cards), the halves, shots, fouls… each its own event id
   *  under one `ParentEventId`. MEASURED off a real `/Betting/PlaceBet` coupon
   *  (Parikara 2026-09-20, Милан–Лечче):
   *
   *    LEG 1  EventId=40535029 ParentEventId=40535029 EventPartTypeId=0 PeriodId=0 PeriodName=""
   *    LEG 2  EventId=40714488 ParentEventId=40535029 EventPartTypeId=4 PeriodId=1 PeriodName="Угловые"
   *
   *  Absent ⇒ the main book, and the coupon falls back to the fixture's own id
   *  with 0/0/"" — which is what every leg carried before 2026-09-20. */
  subEvent?: {
    /** The sub-event's own `Id` → the leg's `EventId`. */
    eventId: number;
    /** `EPTId` → `EventPartTypeId`. ⛔ Not unique on its own: both halves are 1. */
    eventPartTypeId: number;
    /** `P` → `PeriodId`. ⛔ A THIRD field: it is neither 0 nor the EPTId. The
     *  halves are (1,1) and (1,2); corners (4,1); cards (5,1). */
    periodId: number;
    /** `PN` → `PeriodName`, e.g. "Угловые". "" on the main book. */
    periodName: string;
  };
}

/** The raw Digitain event fields `/Betting/PlaceBet` copies into each coupon item.
 *  Sourced from the operator's own event object (`GetBetBuilderStakesByEvent` /
 *  `Common/GetEvent`). */
export interface PlaceBetEvent {
  id: number;
  /** ISO-8601 UTC kick-off (`D`). */
  date: string;
  sportId: number;
  /** Localized sport name (`SN`). */
  sportName: string;
  /** Always-English sport name (`ESN`). */
  sportEn: string;
  /** Digitain country/region id (`RId` → coupon `CountryId`). */
  countryId: number;
  /** Always-English country name (`ECtN`). */
  countryEn: string;
  home: string;
  away: string;
  homeEn: string;
  awayEn: string;
  /** Digitain tournament/championship id (`CId`/`CHId` → coupon `TournamentId`). */
  tournamentId: number;
  /** Localized tournament name (`CN`). */
  tournamentName: string;
  /** Always-English tournament name (`ECN`). */
  tournamentEn: string;
  /** Digitain team ids when known; null when the feed did not send them (the live
   *  board does not — team ids arrive only in the account payloads). The captured
   *  coupon carried them in `Event.Teams[]` while `HomeTeamId`/`AwayTeamId` sat at 0. */
  homeTeamId: number | null;
  awayTeamId: number | null;
}

/** The operator/geo triple + currency the coupon must carry, inherited from the
 *  captured request template — never invented. */
export interface PlaceBetAuth {
  partnerId: number;
  langId: number;
  countryCode: string;
  /** e.g. "TRY" — the session currency short name. */
  currencyShortName: string;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** Build ONE coupon item — shared by `CheckedStakesList` (Amount 0) and
 *  `StakesList` (Amount = stake), which differ only in `Amount` and
 *  `BetBuilderBoostRuleId` (0 vs null — the operator's own inconsistency, kept). */
function buildItem(
  ev: PlaceBetEvent,
  legs: BetBuilderLeg[],
  amount: number,
  factor: number,
  inStakesList: boolean,
): Record<string, unknown> {
  return {
    ShowSign: false,
    StakeId: null,
    StakeName: null,
    Argument: null,
    EventId: ev.id,
    ProviderEventId: null,
    EventDate: ev.date,
    EventDateTimeStamp: 0,
    SportId: ev.sportId,
    SportName: ev.sportName,
    ESN: ev.sportEn,
    CountryId: ev.countryId,
    ECtN: ev.countryEn,
    EventName: null,
    // Digitain's own payload fills `EGN` with the SPORT's English name here
    // (== `ESN`), not the event name — reproduced, not "fixed".
    EGN: ev.sportEn,
    HomeTeam: ev.home,
    EHT: ev.homeEn,
    AwayTeam: ev.away,
    EAT: ev.awayEn,
    HomeTeamId: 0,
    AwayTeamId: 0,
    PeriodName: null,
    SEPN: false,
    TournamentId: ev.tournamentId,
    TournamentName: ev.tournamentName,
    ETN: ev.tournamentEn,
    StakeTypeName: null,
    StakeFullName: ":  ",
    MatchIsLive: false,
    IsExpressDenied: false,
    IsOrdinarDenied: false,
    IsSystemDenied: false,
    IsOfferMarket: false,
    IsOutright: false,
    RealExpressMinLevel: 0,
    Factor: factor,
    NewFactor: 0,
    FactorDelta: 0,
    Limit: null,
    InterrelatedStake: false,
    Deleted: false,
    Updated: false,
    Checked: true,
    ParentEventId: 0,
    ReferenceEventList: [],
    IsCashoutEnabled: false,
    ExpressMinLevel: 2,
    IsInLive: false,
    BetItemStatus: 0,
    IsRemove: false,
    BetItemType: 0,
    Score: "",
    IsSuperTip: false,
    HS: null,
    HomeScore: null,
    AS: null,
    AwayScore: null,
    SetAltScore: "",
    Odds: null,
    Event: {
      Teams: [
        { Side: 1, Id: ev.homeTeamId ?? 0, Name: ev.home },
        { Side: 2, Id: ev.awayTeamId ?? 0, Name: ev.away },
      ],
      Category: { Id: ev.id, Name: null },
    },
    LiveEventTime: 0,
    StakeTypeId: 0,
    StakeCode: 0,
    IsFavorite: false,
    Amount: amount,
    ErrorMessage: null,
    CardId: null,
    CardBoostPercent: null,
    IsDuplicate: false,
    DictFactors: {},
    Factors: [{ Id: 1, Name: null, F: factor, M: 0, NPP: [] }],
    // ⛔ PER LEG, FROM THE LEG'S OWN SUB-EVENT. These five were hard-coded to
    // the fixture (`ev.id`, 0, 0, "") because the only coupon ever captured had
    // main-book legs only. The 2026-09-20 capture has two sub-events in one
    // coupon, and they differ in every one of them — see `BetBuilderLeg.subEvent`.
    BetBuilderStakes: legs.map((l) => ({
      StakeId: l.stakeId,
      EventId: l.subEvent ? l.subEvent.eventId : ev.id,
      StakeTypeName: l.stakeTypeName,
      Argument: l.argument,
      StakeName: l.stakeName,
      PeriodName: l.subEvent ? l.subEvent.periodName : "",
      SEPN: false,
      Deleted: false,
      IsInLive: false,
      ErrorCode: 0,
      StakeTypeId: l.stakeTypeId,
      SportId: ev.sportId,
      StakeCode: l.stakeCode,
      HomeScore: null,
      AwayScore: null,
      EventPartTypeId: l.subEvent ? l.subEvent.eventPartTypeId : 0,
      ShowSign: false,
      HasBoreDraw: false,
      HasEarlyPayout: false,
      // ⛔ THE PARENT, ALWAYS — it is the same on every leg of the coupon and it
      // is the FIXTURE, never the sub-event. Confirmed on the captured coupon:
      // both legs carry 40535029 while their EventIds are 40535029 and 40714488.
      ParentEventId: ev.id,
      TournamentId: ev.tournamentId,
      IsSpecialMarket: false,
      IsBetOnPlayer: false,
      HasRetirementInsurance: false,
      PeriodId: l.subEvent ? l.subEvent.periodId : 0,
    })),
    BetBuilderGroupId: 1,
    ReplacedStakeId: null,
    OfferTypes: [],
    IsSpecialMarket: false,
    IsBetOnPlayer: false,
    IsSpecial: false,
    EventPartTypeId: 0,
    CardType: 0,
    InitialFactor: factor,
    HomeSideIds: null,
    AwaySideIds: null,
    BBGuid: EMPTY_GUID,
    EachWay: false,
    CastProperties: null,
    LpAvailable: false,
    SpAvailable: false,
    EachWayAvailable: false,
    BogAvailable: false,
    EachWayFactor: null,
    PlaceNum: 0,
    TempId: EMPTY_GUID,
    BoostType: 0,
    // The operator sends 0 in CheckedStakesList and null in StakesList — kept.
    BetBuilderBoostRuleId: inStakesList ? null : 0,
    IsDummy: false,
    IsAmericalOddsRestrictionApplied: false,
  };
}

/** Build the full `/Betting/PlaceBet` body (`{cp, countryCode, langId, partnerId}`)
 *  for a same-match bet-builder combo. Pure — no I/O, no template access. */
export function buildPlaceBetCoupon(
  ev: PlaceBetEvent,
  legs: BetBuilderLeg[],
  amount: number,
  factor: number,
  auth: PlaceBetAuth,
  requestId: string,
): Record<string, unknown> {
  const checked = buildItem(ev, legs, 0, factor, false);
  const staked = buildItem(ev, legs, amount, factor, true);
  return {
    cp: {
      UserId: 0,
      IsSuperTotoChecked: false,
      IsSuperTotoConfirmed: false,
      IsLastCoupon: false,
      CheckedStakesList: [checked],
      StakeCount: 1,
      BetTypeName: "COUPON_ORDINAR_NAME",
      IsEmpty: false,
      CheckedItemsCount: 1,
      StatusMessage: null,
      Bets: [],
      StakesList: [staked],
      SimilarStakes: {},
      IgnoreFactorChanges: false,
      IsOutOfRiskFaktor: false,
      FactorChangesSmaller: false,
      FactorChangesHigher: false,
      IgnoreArgumentChanges: false,
      BetWalletType: 0,
      LanguageId: auth.langId,
      ApplicationType: 3,
      ViewType: 1,
      CouponBetMode: 0,
      BetedWithFactorChange: false,
      BetAmount: amount,
      BetTaxAmount: 0,
      PossibleWinTaxAmount: 0,
      PossibleNetWinning: 0,
      BetMinLimit: 0,
      BetMaxLimit: 0,
      PossibleWin: 0,
      BetType: 1,
      SystemIndex: "",
      BettingBonusList: null,
      BettingBonusType: 0,
      BettingRuleId: 0,
      IsBetPlaced: false,
      WinMultipliedFactor: 0,
      MultipliedWinCalculationType: 0,
      WinMultipliedUrl: null,
      OrderNumber: 0,
      TransactionId: 0,
      CheckNumber: 0,
      IsBetEdit: false,
      InitialWinAmount: 0,
      MaxWinAmount: 0,
      BettingBonusValue: 0,
      BettingBonusTypeName: null,
      BetFactor: 0,
      IsCouponUpdated: false,
      StatusException: null,
      IsUnlimitBet: false,
      DayMbetId: 0,
      IsApiBet: false,
      SportLotoNumbers: [],
      FD: 0,
      // Digitain's own typo, reproduced (the partner id).
      PartneId: auth.partnerId,
      IsGeneratedBet: false,
      IsGetLuckyWheelSpin: false,
      ComboBetAmounts: [],
      PartnerUserBonusType: 0,
      JackpotName: null,
      JackpotLevelName: null,
      JackpotLevelId: 0,
      JackpotWinAmount: 0,
      IsJackpotWin: false,
      LiveBetWaitTime: 0,
      ExpressBonusFactor: 0,
      ExpressMinStakesCount: null,
      SystemMinStakesCount: null,
      SuccessType: 0,
      // Semantics unverified (the live payload carried 1210); 0 is the honest
      // default and is what the field reads on an un-submitted coupon.
      TimeStamp: 0,
      OfferTypes: [],
      ExternalBonusId: 0,
      Currency: 0,
      SecurityKey: null,
      CashBackAmount: 0,
      CashBackUltraAmount: 0,
      RequestId: requestId,
      CurrencyShortName: auth.currencyShortName,
      CallbackData: null,
      IsStrikeBet: false,
      StrikeBetSimulationResults: [],
      IsStrikeBetWon: false,
      CardId: EMPTY_GUID,
      CardType: 0,
      ExpertId: null,
      ExpertOrderNumber: null,
    },
    countryCode: auth.countryCode,
    langId: auth.langId,
    partnerId: auth.partnerId,
  };
}

/** The bet-builder board for one event — the raw event (already in the PlaceBet
 *  coupon's shape) plus every selectable stake with its market metadata. From
 *  `Common/GetBetBuilderStakesByEvent`. */
export interface BetBuilderBoard {
  event: PlaceBetEvent;
  stakes: BetBuilderLeg[];
}

/** What `/Betting/PlaceBet` answered. `order` is the minted OrderNumber on success;
 *  `status` carries the operator's refusal (StatusException / StatusMessage) when the
 *  coupon was not placed, so the caller can say WHY rather than just "refused". */
export interface PlaceBetResult {
  order: number | null;
  status: string | null;
}

/** Product of the leg odds, rounded to 2dp — what `Factor`/`InitialFactor` carry.
 *  `Factor` must equal the product of the individual leg factors or Digitain's
 *  price-change guard will refuse the coupon. */
export function combinedFactor(legOdds: number[]): number {
  const p = legOdds.reduce((acc, o) => acc * o, 1);
  return Math.round(p * 100) / 100;
}
